import { useMemo, useRef, useState, type JSX } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { canWrite } from '@cca/corpus-shared';
import { ApiError, api } from '../api/client';
import { useAppContext } from '../frame/AppShell';
import { parseDocx } from './docxParse';
import { mapToDoc, type MappedImport } from './mapToDoc';

const TYPE_LABEL: Record<string, string> = {
  heading: 'Header',
  rubric: 'Rubric',
  spoken: 'Leader',
  response: 'Congregation',
};

/** Import a .docx → preview the semantic mapping → create the document. */
export function ImportPage(): JSX.Element {
  const { session } = useAppContext();
  const navigate = useNavigate();
  const fileRef = useRef<HTMLInputElement>(null);
  const [mapped, setMapped] = useState<MappedImport | null>(null);
  const [title, setTitle] = useState('');
  const [gdocUrl, setGdocUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const notesByTemp = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of mapped?.doc.commentary ?? [])
      m.set(c.targetTempId, (m.get(c.targetTempId) ?? 0) + 1);
    return m;
  }, [mapped]);

  if (!canWrite(session.tier)) {
    return (
      <div className="stack" style={{ maxWidth: 'var(--maxw-reading)' }}>
        <div className="alert alert-danger">Importing is for the Commission only.</div>
        <Link to="/read" className="back-link">
          ← All documents
        </Link>
      </div>
    );
  }

  const onPick = async (file: File) => {
    setError(null);
    setBusy(true);
    try {
      const result = mapToDoc(await parseDocx(file));
      setMapped(result);
      setTitle(result.doc.title);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not read that file.');
      setMapped(null);
    } finally {
      setBusy(false);
    }
  };

  const onPickGdoc = async () => {
    if (!gdocUrl.trim()) return;
    setError(null);
    setBusy(true);
    try {
      const parsed = await api.parseGdoc(gdocUrl.trim());
      const result = mapToDoc({
        paragraphs: parsed.paragraphs,
        footnotes: new Map(Object.entries(parsed.footnotes)),
      });
      setMapped(result);
      setTitle(result.doc.title);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not read that Google Doc.');
      setMapped(null);
    } finally {
      setBusy(false);
    }
  };

  const doImport = async () => {
    if (!mapped) return;
    setBusy(true);
    setError(null);
    try {
      const { id } = await api.importDocument({
        ...mapped.doc,
        title: title.trim() || 'Imported document',
      });
      navigate(`/read/${encodeURIComponent(id)}`);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Import failed. Please try again.');
      setBusy(false);
    }
  };

  const c = mapped?.counts;

  return (
    <div className="stack" style={{ maxWidth: 'var(--maxw-reading)' }}>
      <div>
        <Link to="/read" className="back-link">
          ← All documents
        </Link>
        <h1
          style={{
            margin: 'var(--space-2) 0',
            fontSize: 'var(--text-xl)',
            color: 'var(--color-structural)',
          }}
        >
          Import a service
        </h1>
        <p className="muted" style={{ margin: 0, fontSize: 'var(--text-sm)' }}>
          Start from a Word document or a Google Doc link. We map its styles to liturgy blocks for
          you to check, then create it — you refine anything in the editor afterwards. (A one-time
          import; the source file is never changed.)
        </p>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="stack" style={{ gap: 'var(--space-3)' }}>
        <div>
          <input
            ref={fileRef}
            type="file"
            accept=".docx"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void onPick(f);
            }}
          />
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy}
            onClick={() => fileRef.current?.click()}
          >
            {mapped ? 'Choose a different file' : 'Choose .docx file'}
          </button>
        </div>

        <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
          <input
            className="be-edit"
            style={{ minHeight: 'auto', flex: 1 }}
            placeholder="Or paste a Google Doc link"
            value={gdocUrl}
            onChange={(e) => setGdocUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !busy) void onPickGdoc();
            }}
          />
          <button
            type="button"
            className="btn"
            disabled={busy || !gdocUrl.trim()}
            onClick={onPickGdoc}
          >
            {busy ? 'Reading…' : 'Read Google Doc'}
          </button>
        </div>
      </div>

      {mapped && c && (
        <>
          <div className="card stack" style={{ gap: 'var(--space-3)' }}>
            <label className="stack" style={{ gap: '0.3rem' }}>
              <span className="muted" style={{ fontSize: 'var(--text-sm)' }}>
                Document title
              </span>
              <input
                className="be-edit"
                style={{ minHeight: 'auto' }}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </label>
            <p className="muted" style={{ margin: 0, fontSize: 'var(--text-sm)' }}>
              {c.headings} headers · {c.rubrics} rubrics · {c.leader} leader · {c.congregation}{' '}
              congregation · {c.notes} notes
            </p>
            <button type="button" className="btn btn-primary" disabled={busy} onClick={doImport}>
              {busy ? 'Importing…' : 'Import this document'}
            </button>
          </div>

          <ul className="import-preview">
            {mapped.doc.nodes.map((n) => {
              const noteN = notesByTemp.get(n.tempId) ?? 0;
              const text = n.lines.map((l) => l.runs.map((r) => r.text).join('')).join(' ');
              return (
                <li
                  key={n.tempId}
                  className={`imp-row imp-${n.type}`}
                  style={{ marginLeft: n.parentTempId ? '1.6rem' : 0 }}
                >
                  <span className="imp-type">{TYPE_LABEL[n.type] ?? n.type}</span>
                  <span className="imp-text">{text || '—'}</span>
                  {noteN > 0 && (
                    <span className="imp-note">
                      {noteN} note{noteN > 1 ? 's' : ''}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}
